// Inbox: จำนวนข้อความ ความเร็วการตอบ และห้องค้าง
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { Brand } from "@contracts/api";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { fmtDuration, fmtInt, pctText } from "@shared/lib/format";
import { fbPageIds, fetchInboxAnalytics, overviewKeys } from "../api/overview-api";
import { OvKpi } from "../components/ov-kpi";
import { OvLoading, OvNote } from "../components/ov-states";

export function InboxBlock({ brand, days }: { brand: Brand; days: number }) {
  const ids = fbPageIds(brand);
  const hasToken = !!wazzupToken();
  const { data, isPending, isError } = useQuery({
    queryKey: overviewKeys.inboxAnalytics(ids, days),
    queryFn: () => fetchInboxAnalytics(ids, days),
    enabled: hasToken && ids.length > 0,
  });

  if (!hasToken)
    return (
      <OvNote>
        ยังไม่ได้เข้าสู่ระบบ Wazzup — <Link to="/setting/staff">เข้าสู่ระบบ</Link> เพื่อดูตัวเลข
        Inbox
      </OvNote>
    );
  if (!ids.length) return <OvNote>แบรนด์นี้ยังไม่ได้ผูกเพจ Facebook</OvNote>;
  if (isPending) return <OvLoading />;
  if (isError || !data) return <OvNote>ดึงข้อมูล Inbox ไม่สำเร็จ</OvNote>;

  const t = data.totals ?? {};
  const resp = data.response ?? {};
  const w = data.waiting ?? {};
  const perPage = data.perPage ?? [];

  // เทียบกับช่วงก่อนหน้า — ไม่มีของช่วงก่อน (หรือเป็น 0) ก็ไม่ต้องบอกเปอร์เซ็นต์
  const delta =
    t.prevIn == null || !t.prevIn
      ? ""
      : `${(t.periodIn ?? 0) >= t.prevIn ? "↑" : "↓"} ${Math.abs(
          Math.round((((t.periodIn ?? 0) - t.prevIn) / t.prevIn) * 100),
        )}% vs ช่วงก่อน`;

  return (
    <>
      {(w.over24h ?? 0) > 0 ? (
        <div className="ov-alert bad">
          ⛔ มี {fmtInt(w.over24h)} ห้องค้างเกิน 24 ชม. ยังไม่ได้ตอบ
        </div>
      ) : (w.total ?? 0) > 0 ? (
        <div className="ov-alert warn">⚠️ มี {fmtInt(w.total)} ห้องรอตอบอยู่</div>
      ) : (
        <div className="ov-alert ok">✅ ตอบครบทุกห้องแล้ว</div>
      )}

      <div className="ov-kpis">
        <OvKpi label="ข้อความลูกค้า" value={fmtInt(t.periodIn)} sub={delta} />
        <OvKpi
          label="ห้องที่เคลื่อนไหว"
          value={fmtInt(t.activeRooms)}
          sub={"ตอบแล้ว " + pctText(t.answeredPct)}
        />
        <OvKpi
          label="เวลาตอบเฉลี่ย (คน)"
          value={fmtDuration(resp.avgHumanMs)}
          sub={resp.humanCount ? `จาก ${fmtInt(resp.humanCount)} ครั้ง` : "ยังไม่มีการตอบ"}
        />
        <OvKpi
          label="ตอบภายใน 1 ชม."
          value={pctText(resp.sla1hPct)}
          sub="เกณฑ์ที่ใช้กันในวงการ"
          tone={resp.sla1hPct != null && resp.sla1hPct < 0.5 ? "is-bad" : undefined}
        />
      </div>

      {perPage.length > 1 && (
        <div className="panel">
          <div className="ov-page-rows">
            {perPage.map((p) => (
              <div className="ov-page-row" key={p.pageId}>
                <span className="grow">{p.pageName || p.pageId}</span>
                <span className="muted">{fmtInt(p.periodIn)} ข้อความ</span>
                <span className={p.over24h ? "txt-bad" : "muted"}>ค้าง {fmtInt(p.waiting)}</span>
                <span className="muted">{fmtDuration(p.avgHumanMs)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
